import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'exigo.mci.theme';

export type Theme = 'light' | 'dark';

/** An explicit choice the person made, or null to follow the OS. */
function storedTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    // sandboxed storage — treat as "no explicit choice"
    return null;
  }
}

function osTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Theme stays entirely client-side (non-sensitive, applied pre-paint) — the only
 * one of the legacy's seven `localStorage` keys that was not moved server-side.
 * See security.md §3 "What moves off the client".
 *
 * Two things worth knowing about the shape here:
 *
 * - An explicit choice wins over the OS *permanently*, but until someone makes
 *   one we keep following `prefers-color-scheme` live. That is why the stored
 *   value is only ever written by `toggle`, never on mount — writing on mount
 *   would silently freeze the very first OS-derived value as if the person had
 *   chosen it, and the app would stop tracking their system theme forever.
 * - The `matchMedia` listener is what makes an OS switch apply to an already-open
 *   tab. The pre-paint guard in `index.html` only runs once at load, so without
 *   this the theme would be correct on load and then drift.
 */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [override, setOverride] = useState<Theme | null>(storedTheme);
  const [os, setOs] = useState<Theme>(osTheme);

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent): void => {
      setOs(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const theme = override ?? os;

  useEffect(() => {
    const root = document.documentElement;

    // Suppress transitions across the swap, then restore them once the new
    // palette has painted. See the `.theme-switching` rule in global.css for
    // why this is necessary rather than cosmetic.
    root.classList.add('theme-switching');
    root.setAttribute('data-theme', theme);

    const restore = (): void => {
      root.classList.remove('theme-switching');
    };

    // Two frames: the first runs before the paint that applies the new
    // colours, the second after it. Removing the class on the first would
    // re-enable transitions in time for them to fire on the swap itself,
    // which is the whole thing being avoided.
    let second = 0;
    const first = window.requestAnimationFrame(() => {
      second = window.requestAnimationFrame(restore);
    });

    // rAF does not fire at all in a hidden or non-compositing tab, which would
    // otherwise leave transitions disabled app-wide until the next theme
    // change. There is no paint to race in that case, so a plain timer is a
    // safe backstop — whichever path runs first wins, and both are idempotent.
    const fallback = window.setTimeout(restore, 100);

    return () => {
      window.cancelAnimationFrame(first);
      window.cancelAnimationFrame(second);
      window.clearTimeout(fallback);
      restore();
    };
  }, [theme]);

  const toggle = useCallback(() => {
    const next: Theme = (override ?? os) === 'light' ? 'dark' : 'light';
    setOverride(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // sandboxed storage — the choice still applies for this page view
    }
  }, [override, os]);

  return { theme, toggle };
}
